import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ICON } from '@core/util/icon';
import {
	ParenthesesForElements,
	PayableToTypes,
	PurchasePayableToTypes,
	SoftwareTransactionFeeDisplayLocation,
	SoftwareTransactionFeeSettings,
} from '@models/businessSettings/settings';
import { dispatch, select } from '@ngxs/store';
import { AtomicModule } from 'src/app/shared/atomic/atomic.module';
import { SegmentedButtonOption } from 'src/app/ui/button-segmented/button-segmented.component';
import { UiModule } from 'src/app/ui/ui.module';
import { SectionComponent } from '../../../../ui/section/section.component';
import { GeneralSettingsSelectors } from './general-settings.selectors';
import {
	HandleSoftwareTransactionFeeChange,
	OverwriteBusinessFields,
} from '@state/business/business.actions';
import { IconsModule } from '@ui/icons/icons.module';
import { MatTooltipModule } from '@angular/material/tooltip';

const SoftwareFeeDisplayOptions: {
	value: SoftwareTransactionFeeDisplayLocation;
	label: string;
}[] = [
	{
		value: SoftwareTransactionFeeDisplayLocation.SOA,
		label: 'Statement of Account',
	},
	{
		value: SoftwareTransactionFeeDisplayLocation.TRUST_LEDGER,
		label: 'Trust Ledger',
	},
	{
		value: SoftwareTransactionFeeDisplayLocation.DO_NOT_DISPLAY,
		label: 'Do Not Display',
	},
];

const PurchaseTlPayableToOptions: SegmentedButtonOption[] = [
	{
		label: 'Vendor Firm',
		id: PurchasePayableToTypes.OTHER_PARTY_FIRM_NAME,
	},
	{
		label: 'Vendor Lawyer',
		id: PurchasePayableToTypes.OTHER_PARTY_LAWYER_NAME,
	},
];

const SaleRefiTlPayableToOptions: SegmentedButtonOption[] = [
	{
		label: 'Firm',
		id: PayableToTypes.FIRM_NAME,
	},
	{
		label: 'Office',
		id: PayableToTypes.OFFICE_NAME,
	},
	{
		label: 'Lawyer',
		id: PayableToTypes.LAWYER_ASSIGNED,
	},
];

const ParenthesesForTypeOptions: SegmentedButtonOption[] = [
	{
		label: 'Receipts',
		id: ParenthesesForElements.RECEIPTS,
	},
	{
		label: 'Payments',
		id: ParenthesesForElements.PAYMENTS,
	},
];

@Component({
	selector: 'app-general-settings',
	imports: [
		SectionComponent,
		AtomicModule,
		FormsModule,
		CommonModule,
		UiModule,
		IconsModule,
		MatTooltipModule,
	],
	templateUrl: './general-settings.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeneralSettingsComponent {
	private overwriteBusinessFields = dispatch(OverwriteBusinessFields);
	private handleSoftwareTransactionFeeChange = dispatch(
		HandleSoftwareTransactionFeeChange,
	);

	public viewModel_ = select(GeneralSettingsSelectors.$viewModel);

	public softwareFeeDisplayOptions = SoftwareFeeDisplayOptions;
	public purchaseTlPayableToOptions = PurchaseTlPayableToOptions;
	public saleRefiTlPayableToOptions = SaleRefiTlPayableToOptions;
	public parenthesesForTypeOptions = ParenthesesForTypeOptions;
	public SoftwareTransactionFeeDisplayLocation =
		SoftwareTransactionFeeDisplayLocation;

	public ICON = ICON;

	onEstPropTaxIncreaseChange(amount: string): void {
		this.overwriteBusinessFields({
			field: 'defaultSettings.estimatedPropertyTaxIncrease',
			value: Number(amount),
		});
	}

	setCastShortFallDesc(description: string): void {
		if (!description) return;

		this.overwriteBusinessFields({
			field: 'defaultSettings.trustLedger.defaultDescriptionCashFall',
			value: description,
		});
	}

	onSoftwareTransFeeDisplayLocationChange(
		displayLocation: SoftwareTransactionFeeDisplayLocation,
	) {
		const feeData: SoftwareTransactionFeeSettings = {
			feeAdjustmentAmount: this.viewModel_().transactionFee.priceChange,
			includeHstInBaseFee: this.viewModel_().transactionFee.includeHstInBaseFee,
			displayIn: displayLocation,
		};

		this.handleSoftwareTransactionFeeChange(
			feeData,
			this.viewModel_().trustLedgerTransactionFeeChangeDetails,
		);
	}

	onSoftwareTransFeeAmountChange(feeAdjustmentAmount: number) {
		if (typeof feeAdjustmentAmount !== 'number' || isNaN(feeAdjustmentAmount))
			return;

		const feeData: SoftwareTransactionFeeSettings = {
			feeAdjustmentAmount,
			displayIn: this.viewModel_().transactionFee.displayLocation,
			includeHstInBaseFee:
				!!this.viewModel_().transactionFee.includeHstInBaseFee,
		};

		this.handleSoftwareTransactionFeeChange(
			feeData,
			this.viewModel_().trustLedgerTransactionFeeChangeDetails,
		);
	}

	onPurchaseTlPayableToChange(payableToParty: PurchasePayableToTypes) {
		this.overwriteBusinessFields({
			field: 'defaultSettings.trustLedger.purchasePayableToInTrust',
			value: payableToParty,
		});
	}

	onSaleRefiTlPayableToChange(payableToParty: PayableToTypes) {
		this.overwriteBusinessFields({
			field: 'defaultSettings.trustLedger.payableToInTrust',
			value: payableToParty,
		});
	}

	setIncludeHstInBaseSoftwareTransFee(includeHstInBaseFee: boolean) {
		const feeData: SoftwareTransactionFeeSettings = {
			feeAdjustmentAmount: this.viewModel_().transactionFee.priceChange,
			includeHstInBaseFee: !!includeHstInBaseFee,
			displayIn: this.viewModel_().transactionFee.displayLocation,
		};

		this.handleSoftwareTransactionFeeChange(
			feeData,
			this.viewModel_().trustLedgerTransactionFeeChangeDetails,
		);
	}

	setUseParenthesesForSale(parenthesesTarget: ParenthesesForElements): void {
		this.overwriteBusinessFields({
			field: 'defaultSettings.useParenthesesFor.sale',
			value: parenthesesTarget,
		});
	}

	setUseParenthesesForPurchase(
		parenthesesTarget: ParenthesesForElements,
	): void {
		this.overwriteBusinessFields({
			field: 'defaultSettings.useParenthesesFor.purchase',
			value: parenthesesTarget,
		});
	}

	setUseParenthesesForRefinance(
		parenthesesTarget: ParenthesesForElements,
	): void {
		this.overwriteBusinessFields({
			field: 'defaultSettings.useParenthesesFor.refinance',
			value: parenthesesTarget,
		});
	}

	setJurisdictionHSTComplianceLetters(value: boolean): void {
		this.overwriteBusinessFields({
			field: 'defaultSettings.jurisdictionHSTComplianceLetters',
			value,
		});
	}
}
